import React, { useState, ReactNode, useEffect } from 'react';
import { TabsContainer, TabHeader, TabButton, TabContent } from './styles';

interface TabsProps {
  children: ReactNode[];
  initialTab?: number;
  align?: 'center' | 'left';
}

interface TabChildProps {
  label: string;
  children: ReactNode;
  disabled?: boolean;
  icon?: string;
  activeIcon?: string;
}

const Tabs: React.FC<TabsProps> & { Tab: React.FC<TabChildProps> } = ({
  children,
  initialTab = 0,
  align,
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [hoveredTab, setHoveredTab] = useState<number | null>(null);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  return (
    <TabsContainer>
      <TabHeader align={align}>
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement<TabChildProps>(child)) {
            const isDisabled = child.props.disabled || false;
            const isActive = activeTab === index;
            const isHovered = hoveredTab === index;
            const iconSrc = isActive || (isHovered && !isDisabled)
              ? child.props.activeIcon ?? child.props.icon
              : child.props.icon;

            return (
              <TabButton
                active={isActive}
                disabled={isDisabled}
                onClick={() => {
                  if (!isDisabled) setActiveTab(index);
                }}
                onMouseEnter={() => setHoveredTab(index)}
                onMouseLeave={() => setHoveredTab(null)}
              >
                {iconSrc && (
                  <img src={iconSrc} alt="" width={20} height={20} />
                )}
                {child.props.label}
              </TabButton>
            );
          }
          return null;
        })}
      </TabHeader>
      <TabContent>{React.Children.toArray(children)[activeTab]}</TabContent>
    </TabsContainer>
  );
};

const Tab: React.FC<TabChildProps> = ({ children }) => {
  return <div>{children}</div>;
};

Tabs.Tab = Tab;

export default Tabs;
